import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { TacoAssetType, Project } from "@amzn/afxtaco-types";

// Store asset blob URLs by projectId and assetType
export type ProjectAssets = Partial<Record<TacoAssetType, string>>;

export interface ProjectsState {
  projects: Record<string, Project>;
  allProjectIds: string[];
  isLoading: boolean;
  error: string | null;
  hasLoaded: boolean;
  /** Token for fetching the next page of projects, null when all loaded */
  nextToken: string | null;
  // Store asset blob URLs by projectId -> assetType -> blobUrl
  projectAssets: Record<string, ProjectAssets>;
  /**
   * Monotonically increasing counter per project, bumped when assets should be
   * considered NEW (asset-generation turn). Drives both re-fetch and the
   * "new content" tab indicators. useProjectAsset watches this to re-fetch.
   */
  assetsStaleCounter: Record<string, number>;
  /**
   * Monotonically increasing counter per project, bumped to force a re-fetch of
   * current assets WITHOUT implying new content (e.g. every turn completion, so
   * tabs that 404'd before a mid-turn migration pick up the now-present files).
   * useProjectAsset watches this; the "new content" indicators do NOT, so this
   * raises no blue dots.
   */
  assetsRefetchCounter: Record<string, number>;
  /**
   * Monotonically increasing counter per project, bumped when the user's uploaded
   * files change. Separate from the asset counters because uploads back no tab:
   * reusing `assetsStaleCounter` would discard every cached asset blob and
   * re-fetch all of them, which is both wasteful and visible as a flash.
   */
  uploadsStaleCounter: Record<string, number>;
}

const initialState: ProjectsState = {
  projects: {},
  allProjectIds: [],
  isLoading: false,
  error: null,
  hasLoaded: false,
  nextToken: null,
  projectAssets: {},
  assetsStaleCounter: {},
  assetsRefetchCounter: {},
  uploadsStaleCounter: {},
};

const ProjectsSlice = createSlice({
  name: "Projects",
  initialState,
  reducers: {
    setLoading(state, action: PayloadAction<boolean>) {
      state.isLoading = action.payload;
    },
    setError(state, action: PayloadAction<string | null>) {
      state.error = action.payload;
    },
    setProjects(state, action: PayloadAction<Project[]>) {
      const projects = action.payload;
      state.projects = {};
      state.allProjectIds = [];

      projects.forEach((project) => {
        state.projects[project.projectId] = project;
        state.allProjectIds.push(project.projectId);
      });

      state.error = null;
      state.hasLoaded = true;
    },
    appendProjects(state, action: PayloadAction<Project[]>) {
      const projects = action.payload;
      projects.forEach((project) => {
        if (!state.projects[project.projectId]) {
          state.projects[project.projectId] = project;
          state.allProjectIds.push(project.projectId);
        }
      });
      state.error = null;
    },
    setNextToken(state, action: PayloadAction<string | null>) {
      state.nextToken = action.payload;
    },
    setProject(state, action: PayloadAction<Project>) {
      const project = action.payload;
      state.projects[project.projectId] = project;
      if (!state.allProjectIds.includes(project.projectId)) {
        state.allProjectIds.push(project.projectId);
      }

      state.error = null;
    },
    addProject(state, action: PayloadAction<Project>) {
      const project = action.payload;
      state.projects[project.projectId] = project;
      if (!state.allProjectIds.includes(project.projectId)) {
        state.allProjectIds.unshift(project.projectId);
      }

      state.error = null;
    },
    updateProject(
      state,
      action: PayloadAction<{ projectId: string; updates: Partial<Project> }>,
    ) {
      const { projectId, updates } = action.payload;

      if (state.projects[projectId]) {
        state.projects[projectId] = {
          ...state.projects[projectId],
          ...updates,
        };
      }

      state.error = null;
    },
    removeProject(state, action: PayloadAction<string>) {
      const projectId = action.payload;

      delete state.projects[projectId];
      state.allProjectIds = state.allProjectIds.filter(
        (id) => id !== projectId,
      );

      state.error = null;
    },
    clearProjects(state) {
      state.projects = {};
      state.allProjectIds = [];
      state.error = null;
      state.hasLoaded = false;
    },
    setProjectAsset(
      state,
      action: PayloadAction<{
        projectId: string;
        assetType: TacoAssetType;
        blobUrl: string;
      }>,
    ) {
      const { projectId, assetType, blobUrl } = action.payload;
      if (!state.projectAssets[projectId]) {
        state.projectAssets[projectId] = {};
      }
      state.projectAssets[projectId][assetType] = blobUrl;
    },
    clearProjectAsset(
      state,
      action: PayloadAction<{
        projectId: string;
        assetType: TacoAssetType;
      }>,
    ) {
      const { projectId, assetType } = action.payload;
      if (state.projectAssets[projectId]) {
        delete state.projectAssets[projectId][assetType];
        if (Object.keys(state.projectAssets[projectId]).length === 0) {
          delete state.projectAssets[projectId];
        }
      }
    },
    clearAllProjectAssets(state, action: PayloadAction<string>) {
      const projectId = action.payload;
      delete state.projectAssets[projectId];
    },
    /**
     * Bumps the stale counter for a project's assets, signalling that all
     * cached asset blob URLs should be discarded and re-fetched. Dispatched
     * by the handleExecutionCompleted thunk after new assets are generated.
     */
    invalidateProjectAssets(state, action: PayloadAction<string>) {
      const projectId = action.payload;
      // Clear cached blob URLs
      delete state.projectAssets[projectId];
      // Bump the counter so useProjectAsset's effect re-runs
      state.assetsStaleCounter[projectId] =
        (state.assetsStaleCounter[projectId] ?? 0) + 1;
    },
    /**
     * Forces useProjectAsset to re-fetch current assets without implying new
     * content. Dispatched on every turn completion so tabs that 404'd before a
     * mid-turn migration pick up the now-present files. Does NOT bump
     * assetsStaleCounter, so it raises no "new content" indicators.
     */
    refetchProjectAssets(state, action: PayloadAction<string>) {
      const projectId = action.payload;
      delete state.projectAssets[projectId];
      state.assetsRefetchCounter[projectId] =
        (state.assetsRefetchCounter[projectId] ?? 0) + 1;
    },
    /**
     * Signals that the project's uploaded files changed, so the Overview's
     * "Files you uploaded" list re-fetches. Dispatched when an upload batch
     * succeeds — nothing else learns about uploads, because they are excluded
     * from both the asset manifest and the additional-asset listing.
     *
     * Touches no cached asset blobs: an upload changes nothing a tab renders.
     */
    invalidateUploadedFiles(state, action: PayloadAction<string>) {
      const projectId = action.payload;
      state.uploadsStaleCounter[projectId] =
        (state.uploadsStaleCounter[projectId] ?? 0) + 1;
    },
  },
});

export const {
  setLoading,
  setError,
  setProjects,
  appendProjects,
  setNextToken,
  setProject,
  addProject,
  updateProject,
  removeProject,
  clearProjects,
  setProjectAsset,
  clearProjectAsset,
  clearAllProjectAssets,
  invalidateProjectAssets,
  refetchProjectAssets,
  invalidateUploadedFiles,
} = ProjectsSlice.actions;

export default ProjectsSlice.reducer;
